import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { EnterOutlined, SoundOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';
import { useCurrentUser } from 'store/users/selectors';
import {
  buildTranslationPracticeListQuery,
  handleCreateTranslationPractice,
  handleDeleteTranslationPractice,
  handleUpdateTranslationPractice,
} from 'store/translationPractice/thunks';
import { useTranslationPractice } from 'store/translationPractice/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { handleTextToSpeech } from 'store/vocabulary/thunks';
import { DeleteModal } from 'components/Dialog';
import Loading from 'components/Loading';
import TextReader from 'components/Reader';
import { useAudio } from 'utils/context/AudioProvider';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import SpellPracticeModal, { type SpellPracticeRecognitionStatus } from './SpellPracticeModal';
import styles from './WordTranslationTable.module.css';

interface SpellModalState {
  rowId: string;
  word: string;
  spanishTranslation: string;
  audioUrl: string;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionInstance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((this: SpeechRecognitionInstance, ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((this: SpeechRecognitionInstance, ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((this: SpeechRecognitionInstance, ev: Event) => void) | null;
  onstart: ((this: SpeechRecognitionInstance, ev: Event) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionCtor {
  new (): SpeechRecognitionInstance;
}

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

const normalizeForCompare = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export interface WordTranslationRow {
  id: string;
  isDraft?: boolean;
  created_by?: string;
  user_id?: string;
  word: string;
  spanish_translation: string;
  spanish_definition: string;
  spanish_sentence: string;
}

export interface WordTranslationTableProps {
  pageId?: string | number;
  bookId?: string | number;
}

type TranslationPracticeDispatch = ThunkDispatch<Record<string, unknown>, undefined, AnyAction>;

type EditableField = keyof Omit<WordTranslationRow, 'id' | 'isDraft'>;

const SPELL_AUDIO_PLAYBACK_RATE = 0.82;

const isClientDraftId = (id: string) => String(id).startsWith('row-');

const mapServerItem = (raw: unknown): WordTranslationRow => {
  const item = raw as Record<string, unknown>;
  const nested = item?.data as Record<string, unknown> | undefined;
  const d = nested && typeof nested === 'object' ? nested : item;
  return {
    id: String(item.id ?? item._id ?? ''),
    created_by: String(item.created_by ?? ''),
    user_id: String(item.user_id ?? ''),
    word: String(d.word ?? ''),
    spanish_translation: String(d.spanish_translation ?? ''),
    spanish_definition: String(d.spanish_definition ?? ''),
    spanish_sentence: String(d.spanish_sentence ?? ''),
  };
};

const isTeacherOwnedRow = (row: WordTranslationRow): boolean =>
  row.created_by === 'teacher' || row.created_by === 'owe_admin';

const isStudentOwnedRow = (row: WordTranslationRow, studentUserId?: string): boolean =>
  row.created_by === 'student' &&
  Boolean(studentUserId) &&
  String(row.user_id) === String(studentUserId);

const WordTranslationTable: React.FC<WordTranslationTableProps> = ({ pageId, bookId }) => {
  const dispatch = useDispatch<TranslationPracticeDispatch>();
  const showAlert = useAlert();
  const currentUser = useCurrentUser();
  const currentClass = useCurrentClass();
  const { stopAudio, playAudio } = useAudio();
  const items = useTranslationPractice();
  const isStudent = currentUser?.role === 'student';
  const fieldFocusRef = useRef<{ id: string; field: EditableField; value: string } | null>(null);

  const [draftRows, setDraftRows] = useState<WordTranslationRow[]>([]);
  const [editedById, setEditedById] = useState<Record<string, Partial<WordTranslationRow>>>({});
  const [createWordLoading, setCreateWordLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; word: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [spellModal, setSpellModal] = useState<SpellModalState | null>(null);
  const [spellLoadingId, setSpellLoadingId] = useState<string | null>(null);
  const [recognitionStatus, setRecognitionStatus] = useState<SpellPracticeRecognitionStatus>('idle');
  const [transcript, setTranscript] = useState<string>('');
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const listenStartTimeoutRef = useRef<number | null>(null);
  const speechSessionEndTimeoutRef = useRef<number | null>(null);
  const lastInterimTranscriptRef = useRef('');
  const speechListenSessionIdRef = useRef(0);

  const stopRecognition = useCallback(() => {
    if (speechSessionEndTimeoutRef.current !== null) {
      window.clearTimeout(speechSessionEndTimeoutRef.current);
      speechSessionEndTimeoutRef.current = null;
    }
    if (listenStartTimeoutRef.current !== null) {
      window.clearTimeout(listenStartTimeoutRef.current);
      listenStartTimeoutRef.current = null;
    }
    speechListenSessionIdRef.current += 1;
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.abort();
    } catch (error) {
      Sentry.captureException(error);
    }
    recognitionRef.current = null;
  }, []);

  const closeSpellModal = useCallback(() => {
    stopRecognition();
    lastInterimTranscriptRef.current = '';
    setRecognitionStatus('idle');
    setTranscript('');
    stopAudio();
    setSpellModal((prev) => {
      if (prev?.audioUrl) {
        URL.revokeObjectURL(prev.audioUrl);
      }
      return null;
    });
  }, [stopAudio, stopRecognition]);

  const openSpellModal = useCallback(
    async (rowId: string, word: string, spanishTranslation: string) => {
      const trimmed = spanishTranslation.trim();
      if (!trimmed) {
        showAlert('No Spanish word available', 'error');
        return;
      }
      if (spellLoadingId) return;
      try {
        setSpellLoadingId(rowId);
        stopAudio();
        const response = await handleTextToSpeech({
          text: trimmed,
          pitch: '',
          volume: '',
          speed: '',
          voice_name: 'es-ES-Standard-A',
          voice_languageCodes: 'es-ES',
        });

        const bytes = response?.body?.data;
        if (!Array.isArray(bytes)) {
          showAlert('Failed to generate audio', 'error');
          return;
        }
        const audioBlob = new Blob([Uint8Array.from(bytes)], { type: 'audio/mp3' });
        const url = URL.createObjectURL(audioBlob);
        setRecognitionStatus('idle');
        setTranscript('');
        setSpellModal({ rowId, word, spanishTranslation: trimmed, audioUrl: url });
        playAudio(url, false, 100, SPELL_AUDIO_PLAYBACK_RATE);
      } catch (error) {
        Sentry.captureException(error);
        showAlert('Something went wrong', 'error');
      } finally {
        setSpellLoadingId(null);
      }
    },
    [playAudio, showAlert, spellLoadingId, stopAudio]
  );

  const replaySpellAudio = useCallback(() => {
    if (!spellModal?.audioUrl) return;
    stopAudio();
    playAudio(spellModal.audioUrl, false, 100, SPELL_AUDIO_PLAYBACK_RATE);
  }, [playAudio, spellModal, stopAudio]);

  const startRecognition = useCallback(() => {
    if (!spellModal) return;
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setRecognitionStatus('unsupported');
      showAlert('Speech recognition is not supported in this browser', 'error');
      return;
    }
    stopRecognition();
    stopAudio();
    try {
      const recognition = new Ctor();
      const phrase = spellModal.spanishTranslation;
      let listenStableIdForEnd = -1;
      recognition.lang = 'es-ES';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 5;

      recognition.onstart = () => {
        listenStableIdForEnd = speechListenSessionIdRef.current;
        lastInterimTranscriptRef.current = '';
        setTranscript('');
        setRecognitionStatus('listening');
      };
      recognition.onresult = (event) => {
        const target = normalizeForCompare(phrase);
        let lastFinal: SpeechRecognitionResult | null = null;
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const segment = event.results[i];
          if (segment.isFinal) {
            lastFinal = segment;
          }
        }
        let fullInterim = '';
        for (let i = 0; i < event.results.length; i += 1) {
          const segment = event.results[i];
          if (!segment.isFinal) {
            fullInterim += segment[0]?.transcript ?? '';
          }
        }
        if (lastFinal) {
          let matched = false;
          let topTranscript = '';
          for (let i = 0; i < lastFinal.length; i += 1) {
            const candidate = lastFinal[i].transcript ?? '';
            if (i === 0) topTranscript = candidate;
            if (normalizeForCompare(candidate) === target) {
              matched = true;
              topTranscript = candidate;
              break;
            }
          }
          setTranscript(topTranscript.trim());
          setRecognitionStatus(matched ? 'success' : 'mismatch');
          try {
            recognition.stop();
          } catch (stopErr) {
            Sentry.captureException(stopErr);
          }
          return;
        }
        const trimmedInterim = fullInterim.trim();
        if (trimmedInterim) {
          lastInterimTranscriptRef.current = trimmedInterim;
          setTranscript(trimmedInterim);
        }
      };
      recognition.onerror = (event) => {
        if (event.error === 'aborted') {
          return;
        }
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          showAlert('Microphone permission denied', 'error');
        } else if (event.error === 'no-speech') {
          setRecognitionStatus((prev) => (prev === 'listening' ? 'error' : prev));
          return;
        }
        setRecognitionStatus('error');
      };
      recognition.onend = () => {
        recognitionRef.current = null;
        speechSessionEndTimeoutRef.current = window.setTimeout(() => {
          speechSessionEndTimeoutRef.current = null;
          if (listenStableIdForEnd < 0 || speechListenSessionIdRef.current !== listenStableIdForEnd) {
            return;
          }
          const fallback = lastInterimTranscriptRef.current.trim();
          let applied = false;
          flushSync(() => {
            setRecognitionStatus((prev) => {
              if (prev !== 'listening') return prev;
              applied = true;
              if (!fallback) {
                return 'error';
              }
              const matched =
                normalizeForCompare(fallback) === normalizeForCompare(phrase);
              return matched ? 'success' : 'mismatch';
            });
          });
          if (applied && fallback) {
            setTranscript(fallback);
          }
        }, 100);
      };

      recognitionRef.current = recognition;
      listenStartTimeoutRef.current = window.setTimeout(() => {
        listenStartTimeoutRef.current = null;
        try {
          if (recognitionRef.current !== recognition) return;
          recognition.start();
        } catch (error) {
          Sentry.captureException(error);
          recognitionRef.current = null;
          setRecognitionStatus('error');
        }
      }, 150);
    } catch (error) {
      Sentry.captureException(error);
      setRecognitionStatus('error');
    }
  }, [showAlert, spellModal, stopAudio, stopRecognition]);

  const handleMicClick = useCallback(() => {
    if (recognitionStatus === 'listening') {
      stopRecognition();
      setRecognitionStatus('idle');
      return;
    }
    startRecognition();
  }, [recognitionStatus, startRecognition, stopRecognition]);

  useEffect(() => {
    return () => {
      if (spellModal?.audioUrl) {
        URL.revokeObjectURL(spellModal.audioUrl);
      }
    };
  }, [spellModal?.audioUrl]);

  useEffect(() => {
    return () => {
      stopRecognition();
    };
  }, [stopRecognition]);

  const listQuery = useMemo(
    () =>
      buildTranslationPracticeListQuery({
        bookId,
        pageId,
        classId: currentClass?.id,
        userRole: currentUser?.role,
        userId: currentUser?.id,
        teacherId: currentUser?.role === 'student' ? currentClass?.teacherId : undefined,
      }),
    [bookId, pageId, currentClass?.id, currentUser?.id, currentUser?.role]
  );

  const serverRows = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const mapped = items.map(mapServerItem).filter((r) => r.id);
    if (isStudent) return mapped;
    return mapped.filter((r) => r.created_by !== 'student');
  }, [items, isStudent]);

  const rows = useMemo(() => [...serverRows, ...draftRows], [serverRows, draftRows]);

  const canEditRow = useCallback(
    (row: WordTranslationRow) => {
      if (row.isDraft || isClientDraftId(row.id)) return false;
      if (isStudent) return isStudentOwnedRow(row, currentUser?.id);
      return true;
    },
    [currentUser?.id, isStudent]
  );

  const isRowReadOnly = useCallback(
    (row: WordTranslationRow) => {
      if (row.isDraft) return false;
      if (isStudent) return isTeacherOwnedRow(row);
      return false;
    },
    [isStudent]
  );

  const canDeleteRow = useCallback(
    (row: WordTranslationRow) => {
      if (row.isDraft || isClientDraftId(row.id)) return false;
      if (isStudent) return isStudentOwnedRow(row, currentUser?.id);
      return row.created_by !== 'student';
    },
    [currentUser?.id, isStudent]
  );

  const showCreatorLabel = useCallback(
    (row: WordTranslationRow) => isStudent && isTeacherOwnedRow(row),
    [isStudent]
  );

  const mergeEdits = useCallback(
    (row: WordTranslationRow) => ({ ...row, ...(editedById[row.id] || {}) }),
    [editedById]
  );

  const updateField = useCallback(
    (id: string, field: EditableField, value: string) => {
      if (isClientDraftId(id)) {
        setDraftRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
        return;
      }
      if (field === 'word') {
        const row = serverRows.find((r) => r.id === id);
        if (!row || !isStudentOwnedRow(row, currentUser?.id)) return;
      }
      setEditedById((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: value },
      }));
    },
    [currentUser?.id, serverRows]
  );

  const handleFieldFocus = useCallback(
    (row: WordTranslationRow, field: EditableField) => {
      if (!canEditRow(row)) return;
      const r = mergeEdits(row);
      fieldFocusRef.current = {
        id: row.id,
        field,
        value: String(r[field] ?? ''),
      };
    },
    [canEditRow, mergeEdits]
  );

  const handleFieldBlur = useCallback(
    async (row: WordTranslationRow, field: EditableField) => {
      if (!canEditRow(row)) return;
      if (pageId === undefined || pageId === null || bookId === undefined || bookId === null) return;
      const snap = fieldFocusRef.current;
      if (!snap || snap.id !== row.id || snap.field !== field) {
        fieldFocusRef.current = null;
        return;
      }
      const r = mergeEdits(row);
      const newVal = String(r[field] ?? '');
      if (snap.value === newVal) {
        fieldFocusRef.current = null;
        return;
      }
      try {
        const data = {
          word: r.word,
          spanish_translation: r.spanish_translation,
          spanish_definition: r.spanish_definition,
          spanish_sentence: r.spanish_sentence,
        };
        await dispatch(
          handleUpdateTranslationPractice(
            row.id,
            {
              data,
              book_id: bookId,
              page: pageId,
              user_id: currentUser?.id,
              class_id: currentClass?.id,
              created_by: currentUser?.role,
            },
            listQuery
          )
        );
        setEditedById((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        showAlert('Updated Successfully', 'success');
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        fieldFocusRef.current = null;
      }
    },
    [
      bookId,
      currentClass?.id,
      currentUser?.id,
      currentUser?.role,
      dispatch,
      canEditRow,
      listQuery,
      mergeEdits,
      pageId,
      showAlert,
    ]
  );

  const commitInFlightRef = useRef(false);

  const commitDraft = useCallback(
    async (id: string) => {
      if (commitInFlightRef.current) return;
      try {
        const draft = draftRows.find((r) => r.id === id && r.isDraft);
        if (!draft || !isClientDraftId(id)) return;
        if (pageId === undefined || pageId === null || bookId === undefined || bookId === null) return;
        commitInFlightRef.current = true;
        setCreateWordLoading(true);
        const data = {
          word: draft.word,
          spanish_translation: draft.spanish_translation,
          spanish_definition: draft.spanish_definition,
          spanish_sentence: draft.spanish_sentence,
        };
        await dispatch(
          handleCreateTranslationPractice(
            {
              data,
              book_id: bookId,
              page: pageId,
              created_by: currentUser?.role,
              user_id: currentUser?.id,
              class_id: currentClass?.id,
            },
            listQuery
          )
        );
        setDraftRows((prev) => prev.filter((r) => r.id !== id));
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        commitInFlightRef.current = false;
        setCreateWordLoading(false);
      }
    },
    [bookId, currentClass?.id, currentUser?.id, currentUser?.role, dispatch, draftRows, listQuery, pageId]
  );

  const addRow = useCallback(() => {
    setDraftRows((prev) => {
      if (prev.some((r) => r.isDraft)) return prev;
      return [
        ...prev,
        {
          id: `row-${Date.now()}`,
          isDraft: true,
          word: '',
          spanish_translation: '',
          spanish_definition: '',
          spanish_sentence: '',
        },
      ];
    });
  }, []);

  const runDelete = useCallback(
    async (id: string) => {
      try {
        if (isClientDraftId(id)) {
          setDraftRows((prev) => prev.filter((r) => r.id !== id));
          return;
        }
        await dispatch(handleDeleteTranslationPractice(id));
        setEditedById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [dispatch]
  );

  const openDeleteDialog = useCallback((id: string, word: string) => {
    setPendingDelete({ id, word });
    setDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      setDeleteLoading(true);
      await runDelete(pendingDelete.id);
      setDeleteModal(false);
      setPendingDelete(null);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setDeleteLoading(false);
    }
  }, [pendingDelete, runDelete]);

  const textAreaClass = (row: WordTranslationRow, extra?: string) =>
    [styles.textArea, isRowReadOnly(row) ? styles.textAreaReadOnly : '', extra].filter(Boolean).join(' ');

  const createWordLoadingRef = useRef(false);
  useEffect(() => {
    createWordLoadingRef.current = createWordLoading;
  }, [createWordLoading]);

  const draftWordKeyDown = useCallback(
    (rowId: string, e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      if (createWordLoadingRef.current || commitInFlightRef.current) return;
      void commitDraft(rowId);
    },
    [commitDraft]
  );

  return (
    <>
      <DeleteModal
        deleteToggle={deleteModal}
        setDeleteToggle={setDeleteModal}
        loading={deleteLoading}
        message={
          pendingDelete
            ? `Are you sure you want to remove ${pendingDelete.word ? `"${pendingDelete.word}"` : 'this'} translation practice?`
            : ''
        }
        onClickCancel={() => setPendingDelete(null)}
        onDelete={() => void confirmDelete()}
        extraContent={null}
      />
      <SpellPracticeModal
        open={spellModal !== null}
        onCancel={closeSpellModal}
        word={spellModal?.word ?? ''}
        spanishTranslation={spellModal?.spanishTranslation ?? ''}
        recognitionStatus={recognitionStatus}
        transcript={transcript}
        onReplay={replaySpellAudio}
        onMicClick={handleMicClick}
      />
      <table border={1} className={`${styles.table} vocabulTable`}>
        <thead>
          <tr className={`${styles.headerRow} vocabulHeadTh`}>
            <th className={styles.thWord}>Word</th>
            <th className={styles.thTranslation}>Spanish word</th>
            <th className={styles.thDefinition}>Spanish definition</th>
            <th className={styles.thSentence}>Spanish sentence</th>
            <th className={styles.thSpell}>Spell</th>
            <th className={styles.thActions}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const r = mergeEdits(row);
            const readOnly = isRowReadOnly(row);
            const isWordReadOnly =
              row.isDraft || (!isStudent && !row.isDraft) || (isStudent && isTeacherOwnedRow(row));
            return (
              <tr className="vocabulHeadTr vocIcons" key={row.id}>
                <td className={`${styles.wordCell} ${styles.cellSpeakWrap}`}>
                  {row.isDraft ? (
                    <div className="vocabInput">
                      <Input.TextArea
                        placeholder="word"
                        value={r.word}
                        disabled={createWordLoading}
                        onChange={(e) => updateField(row.id, 'word', e.target.value.trimStart())}
                        onKeyDown={(e) => draftWordKeyDown(row.id, e)}
                        autoSize
                        autoFocus
                        classNames={{ textarea: styles.wordDraftTextarea }}
                      />
                      <div style={{ position: 'absolute', top: 6, right: 4 }}>
                        {createWordLoading ? (
                          <Loading size={17} white />
                        ) : (
                          <EnterOutlined
                            onClick={() => {
                              if (createWordLoadingRef.current || commitInFlightRef.current) return;
                              void commitDraft(row.id);
                            }}
                            style={{ fontSize: '25px' }}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <TextReader stopAudio={stopAudio} text={r.word} language="en-US" style={{ float: 'right' }} />
                      <TextArea
                        readOnly={isWordReadOnly}
                        value={r.word}
                        autoSize
                        className={[
                          styles.textArea,
                          isWordReadOnly ? styles.textAreaReadOnly : '',
                          styles.textAreaWord,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onChange={(e) => updateField(row.id, 'word', e.target.value.trimStart())}
                        onFocus={() => handleFieldFocus(row, 'word')}
                        onBlur={() => void handleFieldBlur(row, 'word')}
                      />
                    </>
                  )}
                </td>
                <td className={styles.cellSpeakWrap}>
                  <TextReader
                    stopAudio={stopAudio}
                    text={r.spanish_translation}
                    language="es-ES"
                    style={{ float: 'right' }}
                  />
                  <TextArea
                    readOnly={readOnly || row.isDraft}
                    value={r.spanish_translation}
                    autoSize
                    className={textAreaClass(row)}
                    onChange={(e) =>
                      updateField(row.id, 'spanish_translation', e.target.value.trimStart())
                    }
                    onFocus={() => handleFieldFocus(row, 'spanish_translation')}
                    onBlur={() => void handleFieldBlur(row, 'spanish_translation')}
                  />
                </td>
                <td className={`${styles.definitionCell} ${styles.cellSpeakWrap}`}>
                  <TextReader
                    stopAudio={stopAudio}
                    text={r.spanish_definition}
                    language="es-ES"
                    style={{ float: 'right' }}
                  />
                  <TextArea
                    readOnly={readOnly || row.isDraft}
                    value={r.spanish_definition}
                    autoSize
                    className={textAreaClass(row, styles.textAreaWide)}
                    onChange={(e) => updateField(row.id, 'spanish_definition', e.target.value.trimStart())}
                    onFocus={() => handleFieldFocus(row, 'spanish_definition')}
                    onBlur={() => void handleFieldBlur(row, 'spanish_definition')}
                  />
                </td>
                <td className={`${styles.sentenceCell} ${styles.cellSpeakWrap}`}>
                  <TextReader
                    stopAudio={stopAudio}
                    text={r.spanish_sentence}
                    language="es-ES"
                    style={{ float: 'right' }}
                  />
                  <TextArea
                    readOnly={readOnly || row.isDraft}
                    value={r.spanish_sentence}
                    autoSize
                    className={textAreaClass(row, styles.textAreaWide)}
                    onChange={(e) => updateField(row.id, 'spanish_sentence', e.target.value.trimStart())}
                    onFocus={() => handleFieldFocus(row, 'spanish_sentence')}
                    onBlur={() => void handleFieldBlur(row, 'spanish_sentence')}
                  />
                </td>
                <td className={styles.spellCell}>
                  <Button
                    type="primary"
                    size="middle"
                    icon={spellLoadingId === row.id ? undefined : <SoundOutlined />}
                    loading={spellLoadingId === row.id}
                    disabled={
                      !r.spanish_translation.trim() ||
                      (spellLoadingId !== null && spellLoadingId !== row.id)
                    }
                    onClick={() => void openSpellModal(row.id, r.word, r.spanish_translation)}
                    className={styles.spellButton}
                  >
                    {spellLoadingId === row.id ? 'Loading' : 'How to Spell'}
                  </Button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {showCreatorLabel(row) ? (
                      <p className={styles.creatorLabel}>
                        {row.created_by === 'owe_admin' ? '(admin)' : '(teacher)'}
                      </p>
                    ) : canDeleteRow(row) ? (
                      <Delete
                        style={{ fontSize: '20px', cursor: 'pointer' }}
                        onClick={() => openDeleteDialog(row.id, r.word)}
                      />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
          <tr className="vocabulHeadTr">
            <td>
              <IconButton>
                <Add
                  style={{ fontSize: '18px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    addRow();
                  }}
                />
              </IconButton>
            </td>
            <td />
            <td />
            <td />
            <td />
            <td />
            {/* <td /> */}
          </tr>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} style={{ border: 'none' }}>
                <p
                  style={{
                    fontWeight: 200,
                    paddingTop: '5px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  No Translation available in this section
                </p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

export default WordTranslationTable;
